const DISALLOWED_CHAR_MATCHER = /[%_<>]/;

/** Overridden error that contains the token that threw it */
export class TokenError extends Error {
	public token: TemplateToken;

	constructor(token: TemplateToken, message: string) {
		super(message);
		this.token = token;
	}
}

/** Base class that all tokens inherit from */
export abstract class TemplateToken {
	line: number;
	position: number;
	originalText: string;
	id: string;
	abstract type: string;
	description?: string;
	defaultValue?: string;
	parameters?: string[];
	section?: string;

	value: string;

	/** Is this considered a user input token, requiring entry by a user */
	abstract IsUserInput(): boolean;
	/** Verify that the token has been created in the proper format with valid parameters */
	public abstract ValidateSource(tokens: TemplateToken[]): void;
	/** Populate the value of the token (if needed) and return it */
	public abstract Populate(tokens: TemplateToken[]): string;

	/** Create a new token of the correct type, based on the 3rd value in the token */
	public static Parse(match: RegExpExecArray, section: string, line: number): TemplateToken {
		const parts = match[1].split(",");
		let token: TemplateToken;
		if (parts.length === 1) {
			// This is a reference to another token somewhere
			token = new ReferenceToken();
			token.parameters = [];
		} else if (parts.length < 4) {
			throw new Error("Invalid token on line " + line + " has " + parts.length + " parts");
		} else {
			switch (parts[3].toLowerCase().trim()) {
				case "section":
					token = new SectionToken();
					token.description = parts[1].trim();
					token.section = token.description;
					section = token.section;
					break;
				case "number":
					token = new NumberToken();
					token.description = parts[1].trim();
					token.defaultValue = parts[2].trim();
					break;
				case "string":
					token = new StringToken();
					token.description = parts[1].trim();
					token.defaultValue = parts[2].trim();
					break;
					case "stringempty":
					token = new StringEmptyToken();
					token.description = parts[1].trim();
					token.defaultValue = parts[2].trim();
					break;
				case "ver":
					token = new VersionToken();
					token.description = parts[1].trim();
					token.defaultValue = parts[2].trim();
					break;
				case "int":
					token = new IntegerToken();
					token.description = parts[1].trim();
					token.defaultValue = parts[2].trim();
					break;
				case "period":
					token = new PeriodToken();
					token.description = parts[1].trim();
					token.defaultValue = parts[2].trim();
					break;
				case "sampcode":
					token = new SampCodeToken();
					token.description = parts[1].trim();
					token.defaultValue = parts[2].trim();
					break;
				case "calcratio":
					token = new CalcRatioToken();
					break;
				case "calcintratio":
					token = new CalcIntRatioToken();
					break;
				case "calcsum":
					token = new CalcSumToken();
					break;
				case "calcproduct":
					token = new CalcProductToken();
					break;
				case "calcmax":
					token = new CalcMaxToken();
					break;
				case "calccagerate":
					token = new CalcCageRateToken();
					break;
				case "calcballrate":
					token = new CalcBallRateToken();
					break;
				case "calcinnerrate":
					token = new CalcInnerRateToken();
					break;
				case "calcouterrate":
					token = new CalcOuterRateToken();
					break;
				default:
					throw new Error("Unknown token type " + parts[3] + " on line " + line);
			}
		}
		// Common to all tokens
		token.line = line;
		token.position = match.index;
		token.originalText = match[0];
		token.id = parts[0].trim();
		token.parameters = parts.slice(4).map(item => item.trim());
		token.value = token.defaultValue;
		token.section = section;
		return token;
	}
}

/** A token that requires GUI input */
export abstract class UserInputToken extends TemplateToken {
	IsUserInput() {
		return true;
	}
}

/** A token that will be calculated from user inputs */
export abstract class CalculatedToken extends TemplateToken {
	IsUserInput() {
		return false;
	}
}

/** A token that is a reference to the result of another token */
export class ReferenceToken extends TemplateToken {
	type = "reference";

	IsUserInput() {
		return false;
	}

	ValidateSource(tokens: TemplateToken[]) {
		if (this.parameters.length !== 0) {
			throw new TokenError(this, "Wrong number of parameters. Expected: 0");
		}
		// Ensure there is a valid reference
		FindReference(tokens, this, this.id);
	}
	Populate(tokens: TemplateToken[]): string {
		const original = FindReference(tokens, this, this.id);
		this.value = original.value;
		return this.value;
	}
}

/** A token that signals the start of a user input section */
export class SectionToken extends TemplateToken {
	type = "section";

	IsUserInput() {
		return false;
	}

	ValidateSource() {
		if (this.parameters.length !== 0) {
			throw new TokenError(this, "Wrong number of parameters. Expected: 0");
		}
		if (DISALLOWED_CHAR_MATCHER.exec(this.description)) {
			throw new Error("Section name cannot contain %, _, <, >");
		}
	}
	Populate(): string {
		this.value = this.description;
		return this.value;
	}
}

/** Any valid number */
export class NumberToken extends UserInputToken {
	type = "number";

	ValidateSource() {
		if (!CheckStringNumeric(this.defaultValue)) {
			throw new TokenError(this, "Default is not numeric");
		}
		if (this.parameters.length !== 0) {
			throw new TokenError(this, "Wrong number of parameters. Expected: 0");
		}
	}
	Populate(): string {
		if (!CheckStringNumeric(this.value)) {
			throw new TokenError(this, "Value " + this.value + " is not numeric");
		}
		return this.value;
	}
}

/** Any non-empty string that is XML safe */
export class StringToken extends UserInputToken {

	type = "string";

	ValidateSource() {
		if (this.parameters.length !== 0) {
			throw new TokenError(this, "Wrong number of parameters. Expected: 0");
		}
		if (DISALLOWED_CHAR_MATCHER.exec(this.defaultValue)) {
			throw new Error("Default cannot contain %, _, <, >");
		}
	}
	Populate(): string {
		if (this.value === "") {
			throw new TokenError(this, "String must have a value");
		}
		if (DISALLOWED_CHAR_MATCHER.exec(this.value)) {
			throw new Error("String cannot contain %, _, <, >");
		}
		return this.value;
	}
}

/** Any (even empty) string that is XML safe */
export class StringEmptyToken extends UserInputToken {
	type = "stringempty";

	ValidateSource() {
		if (this.parameters.length !== 0) {
			throw new TokenError(this, "Wrong number of parameters. Expected: 0");
		}
		if (DISALLOWED_CHAR_MATCHER.exec(this.defaultValue)) {
			throw new Error("Default cannot contain %, _, <, >");
		}
	}
	Populate(): string {
		if (DISALLOWED_CHAR_MATCHER.exec(this.value)) {
			throw new Error("String cannot contain %, _, <, >");
		}
		return this.value;
	}
}

export class VersionToken extends UserInputToken {
	type = "ver";

	ValidateSource() {
		const parts = this.defaultValue.split(".");
		if (parts.length !== 3) {
			throw new TokenError(this, "Version must have 3 numeric parts separated by periods");
		}
		for (const part in parts) {
			if (!CheckStringNumeric(part)) {
				throw new TokenError(this, "Version must have 3 numeric parts separated by periods");
			}
		}
		if (this.parameters.length !== 0) {
			throw new TokenError(this, "Wrong number of parameters. Expected: 0");
		}
	}
	Populate(): string {
		const parts = this.value.split(".");
		if (parts.length !== 3) {
			throw new TokenError(this, "Version must have 3 numeric parts separated by periods");
		}
		for (const part in parts) {
			if (!CheckStringNumeric(part)) {
				throw new TokenError(this, "Version must have 3 numeric parts separated by periods");
			}
		}
		return this.value;
	}
}

export class IntegerToken extends UserInputToken {
	type = "int";

	ValidateSource() {
		if (!CheckStringNumericInt(this.defaultValue)) {
			throw new TokenError(this, "Default is not an integer");
		}
		if (this.parameters.length !== 0) {
			throw new TokenError(this, "Wrong number of parameters. Expected: 0");
		}
	}
	Populate(): string {
		if (!CheckStringNumericInt(this.value)) {
			throw new TokenError(this, "Value " + this.value + " is not an integer");
		}
		return this.value;
	}
}

export class PeriodToken extends UserInputToken {
	type = "period";

	ValidateSource() {
		if (!CheckStringPeriod(this.defaultValue)) {
			throw new TokenError(this, "Default is not a period (numeric and H/M)");
		}
		if (this.parameters.length !== 0) {
			throw new TokenError(this, "Wrong number of parameters. Expected: 0");
		}
	}
	Populate(): string {
		if (!CheckStringPeriod(this.value)) {
			throw new TokenError(this, "Value " + this.value + " is not a period (numeric and H/M)");
		}
		return this.value;
	}
}

export class SampCodeToken extends UserInputToken {
	type = "sampcode";

	public get allowedCodes() {
		return ["93750", "46875", "23438", "11719", "5859", "2930", "1465", "732", "366"];
	}

	ValidateSource() {
		if (!this.allowedCodes.includes(this.defaultValue)) {
			throw new TokenError(this, "Default is not a valid samplecode");
		}
		if (this.parameters.length !== 0) {
			throw new TokenError(this, "Wrong number of parameters. Expected: 0");
		}
	}
	Populate(): string {
		if (!this.allowedCodes.includes(this.defaultValue)) {
			throw new TokenError(this, "Value " + this.value + " is not a valid samplecode");
		}
		return this.value;
	}
}

export class CalcRatioToken extends CalculatedToken {
	type = "calcratio";

	ValidateSource(tokens: TemplateToken[]) {
		if (this.parameters.length !== 2) {
			throw new TokenError(this, "Wrong number of parameters. Expected: 2");
		}
		const param1 = FindReference(tokens, this, this.parameters[0]);
		const param2 = FindReference(tokens, this, this.parameters[1]);
		CheckParamsUserInputOrBefore(this, [param1, param2]);
	}
	Populate(tokens: TemplateToken[]): string {
		const param1 = FindReference(tokens, this, this.parameters[0]);
		const param2 = FindReference(tokens, this, this.parameters[1]);
		try {
			this.value = (Number(param1.value) / Number(param2.value)).toString();
			return this.value;
		} catch (err) {
			throw new TokenError(this, err.message);
		}
	}
}

export class CalcIntRatioToken extends CalculatedToken {
	type = "calcintratio";

	ValidateSource(tokens: TemplateToken[]) {
		if (this.parameters.length !== 2) {
			throw new TokenError(this, "Wrong number of parameters. Expected: 2");
		}
		const param1 = FindReference(tokens, this, this.parameters[0]);
		if (!CheckStringNumeric(this.parameters[1])) {
			throw new TokenError(this, "Parameter 2 must be a number");
		}
		CheckParamsUserInputOrBefore(this, [param1]);
	}
	Populate(tokens: TemplateToken[]): string {
		const param1 = FindReference(tokens, this, this.parameters[0]);
		try {
			this.value = Math.round(Number(param1.value) / Number(this.parameters[1])).toString();
			return this.value;
		} catch (err) {
			throw new TokenError(this, err.message);
		}
	}
}

export class CalcMaxToken extends CalculatedToken {
	type = "calcmax";

	ValidateSource(tokens: TemplateToken[]) {
		if (this.parameters.length !== 2) {
			throw new TokenError(this, "Wrong number of parameters. Expected: 2");
		}
		const param1 = FindReference(tokens, this, this.parameters[0]);
		const param2 = FindReference(tokens, this, this.parameters[1]);
		CheckParamsUserInputOrBefore(this, [param1, param2]);
	}
	Populate(tokens: TemplateToken[]): string {
		const param1 = FindReference(tokens, this, this.parameters[0]);
		const param2 = FindReference(tokens, this, this.parameters[1]);
		try {
			this.value = Math.max(Number(param1.value), Number(param2.value)).toString();
			return this.value;
		} catch (err) {
			throw new TokenError(this, err.message);
		}
	}
}

export class CalcSumToken extends CalculatedToken {
	type = "calcsum";

	ValidateSource(tokens: TemplateToken[]) {
		if (this.parameters.length !== 2) {
			throw new TokenError(this, "Wrong number of parameters. Expected: 2");
		}
		const param1 = FindReference(tokens, this, this.parameters[0]);
		const param2 = FindReference(tokens, this, this.parameters[1]);
		CheckParamsUserInputOrBefore(this, [param1, param2]);
	}
	Populate(tokens: TemplateToken[]): string {
		const param1 = Number(FindReference(tokens, this, this.parameters[0]).value);
		const param2 = Number(FindReference(tokens, this, this.parameters[1]).value);
		try {
			this.value = (param1 + param2).toString();
			return this.value;
		} catch (err) {
			throw new TokenError(this, err.message);
		}
	}
}

export class CalcProductToken extends CalculatedToken {
	type = "calcproduct";

	ValidateSource(tokens: TemplateToken[]) {
		if (this.parameters.length !== 2) {
			throw new TokenError(this, "Wrong number of parameters. Expected: 2");
		}
		const param1 = FindReference(tokens, this, this.parameters[0]);
		const param2 = FindReference(tokens, this, this.parameters[1]);
		CheckParamsUserInputOrBefore(this, [param1, param2]);
	}
	Populate(tokens: TemplateToken[]): string {
		const param1 = Number(FindReference(tokens, this, this.parameters[0]).value);
		const param2 = Number(FindReference(tokens, this, this.parameters[1]).value);
		try {
			this.value = (param1 * param2).toString();
			return this.value;
		} catch (err) {
			throw new TokenError(this, err.message);
		}
	}
}

export class CalcCageRateToken extends CalculatedToken {
	type = "calccagerate";

	ValidateSource(tokens: TemplateToken[]) {
		if (this.parameters.length !== 3) {
			throw new TokenError(this, "Wrong number of parameters. Expected: 3");
		}
		const rollDia = FindReference(tokens, this, this.parameters[0]);
		const pitchDia = FindReference(tokens, this, this.parameters[1]);
		const contAng = FindReference(tokens, this, this.parameters[2]);
		CheckParamsUserInputOrBefore(this, [rollDia, pitchDia, contAng]);
	}
	Populate(tokens: TemplateToken[]): string {
		try {
			const rollDia = Number(FindReference(tokens, this, this.parameters[0]).value);
			const pitchDia = Number(FindReference(tokens, this, this.parameters[1]).value);
			const contAng = Number(FindReference(tokens, this, this.parameters[2]).value);
			this.value = (0.5 * (1 - rollDia / pitchDia * Math.cos(contAng * Math.PI / 180.0))).toString();
			// ½ * (1 – rollerDia/pitchDia *  cos(contAng * pi/  180))
			return this.value;
		} catch (err) {
			throw new TokenError(this, err.message);
		}
	}
}

export class CalcBallRateToken extends CalculatedToken {
	type = "calcballrate";

	ValidateSource(tokens: TemplateToken[]) {
		if (this.parameters.length !== 3) {
			throw new TokenError(this, "Wrong number of parameters. Expected: 3");
		}
		const rollDia = FindReference(tokens, this, this.parameters[0]);
		const pitchDia = FindReference(tokens, this, this.parameters[1]);
		const contAng = FindReference(tokens, this, this.parameters[2]);
		CheckParamsUserInputOrBefore(this, [rollDia, pitchDia, contAng]);
	}
	Populate(tokens: TemplateToken[]): string {
		try {
			const rollDia = Number(FindReference(tokens, this, this.parameters[0]).value);
			const pitchDia = Number(FindReference(tokens, this, this.parameters[1]).value);
			const contAng = Number(FindReference(tokens, this, this.parameters[2]).value);
			this.value = (pitchDia / rollDia * (1 - Math.pow(rollDia / pitchDia * Math.cos(contAng * Math.PI / 180.0), 2))).toString();
			// pitchDia/rollDia * (1 – (rollerDia/pitchDia * cos(contAng * pi/ 180))^2)
			return this.value;
		} catch (err) {
			throw new TokenError(this, err.message);
		}
	}
}

export class CalcInnerRateToken extends CalculatedToken {
	type = "calcinnerrate";

	ValidateSource(tokens: TemplateToken[]) {
		if (this.parameters.length !== 4) {
			throw new TokenError(this, "Wrong number of parameters. Expected: 3");
		}
		const rollDia = FindReference(tokens, this, this.parameters[0]);
		const pitchDia = FindReference(tokens, this, this.parameters[1]);
		const contAng = FindReference(tokens, this, this.parameters[2]);
		const numElement = FindReference(tokens, this, this.parameters[3]);
		CheckParamsUserInputOrBefore(this, [rollDia, pitchDia, contAng, numElement]);
	}
	Populate(tokens: TemplateToken[]): string {
		try {
			const rollDia = Number(FindReference(tokens, this, this.parameters[0]).value);
			const pitchDia = Number(FindReference(tokens, this, this.parameters[1]).value);
			const contAng = Number(FindReference(tokens, this, this.parameters[2]).value);
			const numElement = Number(FindReference(tokens, this, this.parameters[3]).value);
			this.value = (0.5 * (1 + rollDia / pitchDia * Math.cos(contAng * Math.PI / 180.0)) * numElement).toString();
			// ½ * (1+rollDia/pitchDia * cos(contAng * pi/180)) * numElement
			return this.value;
		} catch (err) {
			throw new TokenError(this, err.message);
		}
	}
}

export class CalcOuterRateToken extends CalculatedToken {
	type = "calcouterrate";

	ValidateSource(tokens: TemplateToken[]) {
		if (this.parameters.length !== 4) {
			throw new TokenError(this, "Wrong number of parameters. Expected: 3");
		}
		const rollDia = FindReference(tokens, this, this.parameters[0]);
		const pitchDia = FindReference(tokens, this, this.parameters[1]);
		const contAng = FindReference(tokens, this, this.parameters[2]);
		const numElement = FindReference(tokens, this, this.parameters[3]);
		CheckParamsUserInputOrBefore(this, [rollDia, pitchDia, contAng, numElement]);
	}
	Populate(tokens: TemplateToken[]): string {
		try {
			const rollDia = Number(FindReference(tokens, this, this.parameters[0]).value);
			const pitchDia = Number(FindReference(tokens, this, this.parameters[1]).value);
			const contAng = Number(FindReference(tokens, this, this.parameters[2]).value);
			const numElement = Number(FindReference(tokens, this, this.parameters[3]).value);
			this.value = (0.5 * (1 - rollDia / pitchDia * Math.cos(contAng * Math.PI / 180.0)) * numElement).toString();
			// ½ * (1 - rollDia/pitchDia * cos(contAng * pi/180)) * numElement
			return this.value;
		} catch (err) {
			throw new TokenError(this, err.message);
		}
	}
}

function CheckStringNumeric(value: string): boolean {
	return (!Number.isNaN(Number(value)));
}

function CheckStringNumericInt(value: string): boolean {
	return (Number.isInteger(Number(value)));
}
function CheckStringPeriod(value: string): boolean {
	const units = value.slice(-1);
	if (units !== "H" && units !== "M") {
		return false;
	}
	const number = value.slice(0, -1);
	return CheckStringNumericInt(number);
}
function CheckParamsUserInputOrBefore(callerToken: CalculatedToken, params: TemplateToken[]) {
	for (const param of params) {
		if (!(param.IsUserInput() || (param instanceof CalculatedToken && (param.line < callerToken.line || (param.line === callerToken.line && param.position < callerToken.position))))) {
			throw new TokenError(callerToken, "Calculation token parameters must be user inputs, or other calculations that appear BEFORE the referencing token");
		}
	}
}
function FindReference(tokens: TemplateToken[], source: ReferenceToken, id: string): TemplateToken {
	const original = tokens.find(item => item.id === id && item.type !== "reference");
	if (!original) {
		throw new TokenError(source, "No token found for reference '" + id + "'");
	}
	return original;
}
